import { Icon } from '../../../components/icons';
import { FeaturePageLayout, type FeatureContent } from './FeaturePageLayout';
import { InboxPlacementStory } from './stories/InboxPlacementStory';

/**
 * Marketing page for the Inbox Placement add-on. Pairs naturally with Warmup —
 * Inbox Placement is the "diagnose" half and Warmup is the "fix" half — so the
 * related-features rail leads with Warmup. Tier strip pulled live from
 * /plans/addons via the layout's useAddonTiers call.
 */
const CONTENT: FeatureContent = {
  kind: 'inbox_placement',
  slug: 'inbox-placement',
  story: <InboxPlacementStory />,
  title: 'Inbox Placement',
  tagline: 'See exactly where your mail lands across Gmail / Yahoo / Outlook.',
  metaDescription:
    'Seed-inbox testing across Gmail, Outlook, Yahoo, ProtonMail and GMX — a per-ESP inbox vs spam vs missing breakdown, with deliverability trends over time.',
  eyebrow: 'Credit-metered · Pro-bundled headroom',
  description:
    'Send a tracking-coded test from your production sender to a list of seed addresses; we report where each one landed — Inbox, Promotions / Updates, Spam, or Missing — per ESP. The 30 / 60 / 90-day reputation trend rolls every completed test into one curve so you can see whether deliverability is improving or regressing.',
  icon: <Icon.Mail width={48} height={48} />,
  accent: 'verify',
  heroBg: '/heroes/feat-inbox-placement.webp',
  pillars: [
    {
      title: 'Real send → real verdict',
      body: "We don't mock placement — your mail server actually sends to real seed mailboxes; we report where each one landed. No simulators, no rule-based guesses, no synthetic deliverability proxies.",
    },
    {
      title: 'Per-ESP breakdown',
      body: 'Gmail / Yahoo / Outlook / Zoho / Other ESPs reported separately — so when 90% lands at Google but 10% lands at Outlook, you know exactly which provider needs the warmup attention.',
    },
    {
      title: 'Reputation trend',
      body: 'Every completed test rolls into a per-sender 30 / 60 / 90-day reputation score so you see whether you’re improving or regressing — at the right cadence for a campaign team.',
    },
  ],
  steps: [
    {
      title: 'Pick a sender',
      body: 'Use the same mailbox you send your real campaigns from — auto-send via stored SMTP, or copy the seed list and fire from your own ESP / sequencer.',
    },
    {
      title: 'Run the test',
      body: 'Each test consumes the credit weight shown in the Pricing section above (refunded automatically if no delivery is detected). Seed list lands within seconds; results within 5–10 minutes typically.',
    },
    {
      title: 'Read the placement',
      body: 'Inbox vs Promotions vs Spam vs Missing per ESP, plus a composite deliverability score 0–100 with a band (Excellent / Good / Mixed / Poor).',
    },
    {
      title: 'Track the trend',
      body: 'Weekly trend page rolls every completed test by sender so you see whether your reputation is improving or regressing over time. Optional Slack / email / Intercom alert when it dips.',
    },
  ],
  included: [
    {
      scope: 'trial',
      items: [
        'A few tests to validate the workflow',
        'Full per-ESP placement detail',
        'Reputation trend rolling forward',
      ],
    },
    {
      scope: 'pro',
      items: [
        'Pro 25k+ plans bundle enough monthly credits to cover an anchor volume of tests',
        'Auto-refund when no email is detected at any seed within the window',
        'AI Co-Pilot recommendation on every completed test',
      ],
    },
    {
      scope: 'addon',
      items: [
        'Top up with a credit pack when your monthly allowance runs low',
        'Auto-send via stored SMTP (optional)',
        'Same per-test credit weight whether credits come from the plan or a pack',
      ],
    },
  ],
  faq: [
    {
      q: 'How does the auto-send option work?',
      a: 'On the Inbox Placement page you can connect your SMTP credentials once (SendGrid, Mailgun, Brevo, Mandrill, Postmark, Google Workspace, your own relay…) and we fire the seed emails through your actual sender. Test reflects your real production reputation rather than asking you to copy-paste seeds into a sequencer.',
    },
    {
      q: 'How are inbox-placement tests priced?',
      a: 'Each test consumes the credit weight shown in the Pricing section above, drawn from your monthly wallet. Inbox placement is the heaviest single call we charge because we send real mail to real seed mailboxes across every major ESP — Pro plans bundle a small free quota to anchor typical usage, and credit packs cover the overflow if you run a large one-off campaign audit.',
    },
    {
      q: 'When is a test refunded?',
      a: 'If no email is detected at any seed within the test window, the credits consumed by the test are refunded automatically. Useful when the sender was misconfigured or the seed list never received anything.',
    },
    {
      q: 'How big is the seed list?',
      a: '~30 seed addresses across Gmail / Yahoo / Outlook / Zoho / Other — statistically representative coverage of "did the email land in the average user\'s inbox" across the major ESPs.',
    },
  ],
  addonKind: 'inbox_placement',
  relatedFeatures: [
    {
      slug: 'warmup',
      title: 'Warmup',
      tagline: 'Fix the deliverability problem Inbox Placement just diagnosed.',
    },
    {
      slug: 'domain-health',
      title: 'Domain Health',
      tagline: 'SPF / DKIM / DMARC posture surfaces next to every placement test.',
    },
    {
      slug: 'blacklist-checks',
      title: 'Blacklist Checks',
      tagline: 'Blacklist hits flag domain-level reputation problems early.',
    },
  ],
};

export function InboxPlacementFeaturePage() {
  return <FeaturePageLayout content={CONTENT} />;
}

// Default export required by React Router framework-mode route modules.
export default InboxPlacementFeaturePage;
